/**
 * KiaanMarkdown — the small markdown subset shown in Kiaan chat (SUN-196): **bold**, [text](url), and
 * "- " / "1. " lists. Built as React nodes (no raw HTML, no dangerouslySetInnerHTML).
 * Links: only http(s) and mailto open as real links. A task link like /SUN/issues/SUN-193 shows its
 * label as plain text (the viewer has no page for it). Any other scheme (javascript:, data:…) shows
 * only the label.
 */
import { Fragment, type ReactNode } from 'react';

const INLINE = /\*\*([^*\n]+)\*\*|\[([^\]\n]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)/g;

export type LinkKind = 'web' | 'task' | 'none';

export function classifyLink(url: string): LinkKind {
  const u = url.trim();
  if (/^\/[A-Za-z0-9_-]+\/issues\/[A-Za-z0-9_-]+\/?$/.test(u)) return 'task';
  if (/^(https?:\/\/|mailto:)/i.test(u)) return 'web';
  return 'none';
}

function inline(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const m of text.matchAll(INLINE)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const key = `${keyBase}-${n++}`;
    if (m[1] !== undefined) {
      out.push(<b key={key}>{m[1]}</b>);
    } else {
      const kind = classifyLink(m[3]);
      if (kind === 'web') {
        out.push(<a key={key} href={m[3]} target="_blank" rel="noopener noreferrer">{m[2]}</a>);
      } else {
        out.push(<span key={key} className={kind === 'task' ? 'kw-ref' : undefined} data-testid={kind === 'task' ? 'kw-task-ref' : undefined}>{m[2]}</span>);
      }
    }
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function KiaanMarkdown({ text }: { text: string }) {
  const lines = text.split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const bullet = /^\s*[-*]\s+(.*)$/;
    const number = /^\s*\d+[.)]\s+(.*)$/;
    const re = bullet.test(lines[i]) ? bullet : number.test(lines[i]) ? number : null;
    if (re) {
      const items: ReactNode[] = [];
      const start = i;
      while (i < lines.length && re.test(lines[i])) {
        items.push(<li key={i}>{inline(lines[i].replace(re, '$1'), `l${i}`)}</li>);
        i++;
      }
      blocks.push(re === bullet ? <ul key={`u${start}`}>{items}</ul> : <ol key={`o${start}`}>{items}</ol>);
    } else {
      blocks.push(<Fragment key={`p${i}`}>{inline(lines[i], `p${i}`)}{i < lines.length - 1 ? '\n' : ''}</Fragment>);
      i++;
    }
  }
  return <>{blocks}</>;
}
